/**
 * Theme Builder
 *
 * Utility for generating MUI themes from tenant branding configuration.
 * Merges custom branding with default theme values to create a complete theme.
 */

import { alpha, createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';
// Type-only augmentation so ThemeOptions.components accepts the MuiPickers*
// keys from @mui/x-date-pickers (used for the dark-mode popup border, #1009).
import type {} from '@mui/x-date-pickers/themeAugmentation';
import type { BrandingConfig } from '@/config/branding';

// OS Buddy / OceanSpace brand palette — exact Figma "Oceanspace 0.2 – SHARED"
// Color styles. main values come straight from the named tokens; light/dark
// variants are tints/shades of those tokens (or the paired Accent 1/2 styles).
//   Blue – Abyss #228ACF · Blue – Shallow #5BD5E6 · Blue – Surface #DDECFE
//   Dark 1 – Deep #0D171C · Dark 2 #151A23 · Dark 5 #606060 · Dark 6 #A2A2A2
//   Light Grey 1 – Foam #FAFAFA · Light Grey 2 #F5F5F5
//   Yellow – Accent 1 #FFD52E / 2 #FFB92E · Red – Accent 1 #FE4B09 / 2 #F14927
//   Green – Accent 1 #AAE526 / 2 #A4D436
const palette = {
  light: {
    primary: {
      main: '#228ACF', // Blue – Abyss
      light: '#4FA4DA',
      dark: '#1A6FA6',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#5BD5E6', // Blue – Shallow
      light: '#84E1EE',
      dark: '#2FB6CC',
      contrastText: '#0D171C',
    },
    success: {
      main: '#A4D436', // Green – Accent 2
      light: '#AAE526', // Green – Accent 1
      dark: '#84AC2B',
      contrastText: '#0D171C',
    },
    warning: {
      main: '#FFB92E', // Yellow – Accent 2
      light: '#FFD52E', // Yellow – Accent 1
      dark: '#D9971F',
      contrastText: '#0D171C',
    },
    error: {
      main: '#FE4B09', // Red – Accent 1
      light: '#F14927', // Red – Accent 2
      dark: '#C93A06',
      contrastText: '#ffffff',
    },
    background: {
      default: '#FAFAFA', // Light Grey 1 – Foam
      paper: '#FFFFFF', // WHITE
    },
    text: {
      primary: '#151A23', // Dark 2
      secondary: '#606060', // Dark 5
    },
  },
  dark: {
    primary: {
      main: '#228ACF', // Blue – Abyss
      light: '#5BD5E6', // Blue – Shallow
      dark: '#1A6FA6',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#5BD5E6', // Blue – Shallow
      light: '#84E1EE',
      dark: '#2FB6CC',
      contrastText: '#0D171C',
    },
    success: {
      // Dark theme renders greens slightly lighter/softer than the light tokens
      main: '#AED34B', // light token #A4D436
      light: '#AAE526', // Green – Accent 1
      dark: '#768F3A', // light token #84AC2B
      contrastText: '#0D171C',
    },
    warning: {
      main: '#FFB92E', // Yellow – Accent 2
      light: '#FFD52E', // Yellow – Accent 1
      dark: '#D9971F',
      contrastText: '#0D171C',
    },
    error: {
      // Dark theme renders the error/orange a touch softer
      main: '#DD5B34', // light token #FE4B09
      light: '#F14927', // Red – Accent 2
      dark: '#C93A06',
      contrastText: '#ffffff',
    },
    background: {
      default: '#13161A', // Dark base surface (deepest)
      paper: '#181B20', // Content & cards
    },
    text: {
      primary: '#FAFAFA', // Primary text / numbers
      secondary: '#C0C3C4', // Secondary text
    },
    divider: '#343537', // Dark borders / dividers
  },
};

/**
 * Dark-theme surface elevation scale from the design system. MUI's palette only
 * exposes background.default/paper, so these extra surfaces (sidebar, elevated
 * blocks) are kept here for components that need them.
 */
export const darkSurfaces = {
  sidebar: '#111111', // Sidebar & top bar (darkest chrome)
  base: '#13161A', // Deepest base background
  card: '#181B20', // Content & cards
  elevated: '#1E2125', // Raised blocks (quick actions)
  border: '#343537', // Borders / dividers
  mutedIcon: '#8D8F91', // Dimmed grey — inactive sidebar icons
} as const;

/**
 * Named spacing tokens — the 8px grid from the OS Buddy design system.
 * Values are in pixels. Prefer `theme.spacing(n)` for MUI sx/styled usage;
 * use these named tokens when a component needs an explicit, documented value
 * instead of a raw px literal.
 */
export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  '2xl': 32,
} as const;

/**
 * How far a standard field's text sits from the edge of its tint (#2209).
 *
 * The value and its label both carry this, so they share one left edge. Pad
 * only the input and the label hangs 8px to its left, which is what made an
 * earlier attempt remove the padding altogether.
 */
const FIELD_INSET = spacing.sm;

const sharedOptions: ThemeOptions = {
  typography: {
    fontFamily: '"Outfit Variable", "Outfit", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.2 },
    h2: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.3 },
    h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
    h4: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
    h5: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
    h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: '0.875rem', lineHeight: 1.5 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
    button: { textTransform: 'none', fontWeight: 500 },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    /**
     * Every icon button is at least a 44 px touch target (#2718).
     *
     * The convention was already written by hand in 68 places — but almost
     * all of them in Settings, which is used at a desk. The mechanic's own
     * screens had it in 1 of 86: start / pause / complete were MUI's default
     * ~30 px, spaced 4 px apart, tapped on a tablet with gloves on.
     *
     * Doing it here rather than per call site means the rule holds for
     * buttons nobody has audited yet, and for ones added later.
     *
     * `size="small"` still controls the visual size — this only guarantees
     * the hit area around it, so nothing in the UI grows. The one exception
     * is a button that deliberately sits inside a dense inline row, which can
     * still opt out with its own sx.
     */
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: 44,
          minHeight: 44,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          // Disabled: muted grey background + faint text per design system
          '&.Mui-disabled': {
            backgroundColor: '#E8E8E8',
            color: '#A2A2A2',
          },
        },
        sizeSmall: { padding: '4px 12px' },
        sizeLarge: { padding: '12px 24px' },
        // Outlined: 1.5px brand border, muted blue hover.
        //
        // Was a hardcoded #DDECFE (Blue – Surface) with no dark variant, so
        // night mode flashed a near-white panel under the cursor — 76× the
        // luminance of the surface behind it (#2756).
        //
        // One tint of the brand blue instead. `primary.main` is the same
        // #228ACF in both palettes, so this is a single hover colour that
        // composites over whichever surface is behind it rather than painting
        // a light block onto a dark one.
        //
        // 0.12, tuned on the running dev build: 0.16 read as too heavy in
        // night mode. The band is narrow at both ends — the outlined label
        // is #228ACF too, so a deeper wash drops it under 3:1 against its
        // own background, while a lighter one stops registering as a hover
        // at all. In daylight this is #E4F1F9, close to the #DDECFE it
        // replaces.
        outlined: ({ theme }) => ({
          borderWidth: 1.5,
          borderColor: theme.palette.primary.main,
          '&:hover': {
            borderWidth: 1.5,
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
          },
          // Outlined disabled keeps a transparent background, only fades text/border
          '&.Mui-disabled': {
            backgroundColor: 'transparent',
            borderColor: '#E8E8E8',
            color: '#A2A2A2',
          },
        }),
      },
      defaultProps: { disableElevation: true },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...(theme.palette.mode === 'dark' && {
            backgroundImage: 'none',
          }),
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 6 } },
    },
    MuiTextField: {
      defaultProps: { size: 'small', variant: 'standard' },
    },
    // The label always sits above the box (#2201). Left to itself it drops into
    // an empty field, and with the tint behind it that reads as a filled-in
    // value rather than a label — the two tell the user opposite things.
    MuiInputLabel: {
      defaultProps: { shrink: true },
      styleOverrides: {
        // Sits above the value, so it carries the same inset. `left` rather
        // than padding: the shrunk label is `scale(0.75)` about its left edge,
        // which would shrink padding with it, and it already owns `transform`.
        root: { left: FIELD_INSET },
      },
    },
    // Select and FormControl do not inherit MuiTextField's defaults (#2148).
    MuiSelect: {
      defaultProps: { variant: 'standard' },
      styleOverrides: {
        // The selected value renders in a div rather than an input, so it needs
        // the inset spelled out to line up with the text fields around it.
        standard: { paddingInline: FIELD_INSET },
      },
    },
    MuiFormControl: {
      defaultProps: { variant: 'standard' },
    },
    // The underline carries focus and error state now that most fields have no
    // frame. Same palette as the outlined border below, which still applies to
    // the app bar's search field.
    //
    // The tint is what makes a wide field read as a field (#2148): a bare
    // standard input is transparent with no padding, so at `fullWidth` all that
    // survives is a 1px line spanning the pane, which the eye takes for a
    // divider. The wash gives the control a body without putting a frame back
    // around it. Black/white at low alpha rather than a fixed hex, so it sits
    // on white paper and on the dark card alike.
    MuiInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
          borderTopLeftRadius: 6,
          borderTopRightRadius: 6,
          // Text flush against the tint's edge looks unfinished, so the value
          // is inset (#2209). The label is inset by the same amount below —
          // pad only one of them and they stop lining up, which is why an
          // earlier attempt at this removed the padding again.
          paddingInline: FIELD_INSET,
          transition: theme.transitions.create('background-color', {
            duration: theme.transitions.duration.shorter,
          }),
          '&:hover:not(.Mui-disabled)': {
            backgroundColor:
              theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
          },
          '&.Mui-focused': {
            backgroundColor:
              theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
          },
          '&.Mui-disabled': { backgroundColor: 'transparent' },
          '&:before': { borderBottomColor: '#C9CDD2', borderBottomWidth: 1 },
          // A touch darker on hover, not text.secondary — that lands near-black
          // in light mode and reads as a focused field when it is only hovered.
          '&:hover:not(.Mui-disabled):before': {
            borderBottomColor: '#9AA0A6',
            borderBottomWidth: 1,
          },
          '&:after': { borderBottomColor: theme.palette.primary.main },
          '&.Mui-error:after': { borderBottomColor: '#FE4B09' },
          // Multiline grows downward, so the tint wraps the whole box rather
          // than just the top corners. Its padding goes on the textarea below,
          // which is the element autosize measures.
          '&.MuiInputBase-multiline': {
            borderRadius: 6,
            paddingBlock: 0,
          },
        }),
        // The label sits above the box, so it must not pick up the inline
        // padding the input has.
        formControl: { 'label + &': { marginTop: 16 } },
        input: { paddingBlock: 6 },
        // Autoresize measures this element, so its padding is the padding the
        // control actually grows by — and every added row keeps its room.
        inputMultiline: { paddingBlock: 6, paddingInline: 0 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          // Rest border per design system
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#C9CDD2',
          },
          // Focus: brand-blue border + 3px blue glow
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: 1,
          },
          '&.Mui-focused': {
            boxShadow: '0 0 0 3px rgba(34, 138, 207, 0.25)',
          },
          // Error: 1.5px error border
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FE4B09',
            borderWidth: 1.5,
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          '@media (pointer: coarse)': {
            fontSize: '16px !important',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { padding: '12px 16px' },
        head: { fontWeight: 600 },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { borderRight: 'none' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          // Dialog elevation per design system
          boxShadow: '0 8px 24px rgba(13, 23, 28, 0.16)',
          ...(theme.palette.mode === 'dark' && {
            backgroundImage: 'none',
            backgroundColor: '#181B20', // dark content/card surface (matches palette.dark.background.paper)
            // Hairline border: elevation shadows alone don't separate a popup
            // surface from the dark background (#1009)
            border: `1px solid ${theme.palette.divider}`,
          }),
          ...(theme.palette.mode === 'light' && {
            backgroundColor: '#F5F5F5', // Light Grey 2
          }),
        }),
      },
    },
    // Popup surfaces (menus, selects, popovers, autocomplete dropdowns, date
    // pickers) get a hairline divider border in dark mode — same rationale as
    // the dialog border above (#1009). Menu and Select render through Popover,
    // so the MuiPopover override covers them all.
    MuiPopover: {
      styleOverrides: {
        paper: ({ theme }) => ({
          ...(theme.palette.mode === 'dark' && {
            border: `1px solid ${theme.palette.divider}`,
          }),
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: ({ theme }) => ({
          ...(theme.palette.mode === 'dark' && {
            border: `1px solid ${theme.palette.divider}`,
          }),
        }),
      },
    },
    MuiPickerPopper: {
      styleOverrides: {
        paper: ({ theme }) => ({
          ...(theme.palette.mode === 'dark' && {
            border: `1px solid ${theme.palette.divider}`,
          }),
        }),
      },
    },
  },
};

/**
 * Build a MUI theme from branding configuration
 *
 * @param branding - Tenant branding configuration (null for defaults)
 * @param mode - Theme mode (light or dark)
 * @returns Complete MUI theme
 */
export function buildThemeFromBranding(
  branding: BrandingConfig | null | undefined,
  mode: 'light' | 'dark'
): Theme {
  const basePalette = mode === 'light' ? palette.light : palette.dark;

  // Merge custom colors with defaults
  const customColors = branding?.colors || {};

  const themeOptions: ThemeOptions = {
    ...sharedOptions,
    palette: {
      mode,
      ...basePalette,
      // Override primary color if provided
      primary: {
        ...basePalette.primary,
        ...(customColors.primary || {}),
      },
      // Override secondary color if provided
      secondary: {
        ...basePalette.secondary,
        ...(customColors.secondary || {}),
      },
    },
  };

  // Custom font family
  if (branding?.typography?.fontFamily) {
    themeOptions.typography = {
      ...themeOptions.typography,
      fontFamily: branding.typography.fontFamily,
    };
  }

  return createTheme(themeOptions);
}

/**
 * Validate if a color string is valid
 * Accepts hex (#RGB, #RRGGBB), rgb(), rgba(), hsl(), hsla()
 */
export function isValidColor(color: string): boolean {
  // Hex color
  if (/^#([0-9A-F]{3}){1,2}$/i.test(color)) {
    return true;
  }
  // rgb/rgba
  if (/^rgba?\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})(,\s*(0|1|0?\.\d+))?\)$/i.test(color)) {
    return true;
  }
  // hsl/hsla
  if (/^hsla?\((\d{1,3}),\s*(\d{1,3})%,\s*(\d{1,3})%(,\s*(0|1|0?\.\d+))?\)$/i.test(color)) {
    return true;
  }
  return false;
}
